import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@kontentino/ui';
import { usePostsContainer } from 'components/posts/context';
import { usePostsLayoutState } from 'app/modules/posts/hooks/usePostsLayoutState';
import CalendarUtils from 'app/utils/calendar';
import DateUtils from 'app/utils/date';
import dayjs from 'dayjs';
import clsx from 'clsx';

interface Props {
  showMobileView?: boolean;
}

const CalendarViewModeSwitch = ({ showMobileView }: Props) => {
  const { t } = useTranslation();
  const { filterState, changeFilter } = usePostsContainer();
  const {
    calendarViewMode,
    setCalendarViewMode,
    setDateViewMode,
    gridMobileViewActive,
    toggleGridMobileView,
  } = usePostsLayoutState();
  const contentRef = useRef<HTMLDivElement>(null);
  const [unrolledWidth, setUnrolledWidth] = useState<number>();

  useEffect(() => {
    const el = contentRef.current;

    if (!el) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;

      if (width > 0) {
        requestAnimationFrame(() => setUnrolledWidth(width));
      }
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  function selectWeek() {
    if (calendarViewMode === 'week') {
      return;
    }

    const { start, end } = CalendarUtils.getWeekRange(
      CalendarUtils.resolveWeekAnchor(filterState.startDate),
    );

    setCalendarViewMode('week');
    changeFilter(
      { key: 'startDate', value: DateUtils.toDateString(start) },
      { key: 'endDate', value: DateUtils.toDateString(end) },
    );
  }

  function selectMonth() {
    if (calendarViewMode === 'month') {
      return;
    }

    const current = dayjs(filterState.startDate);

    setCalendarViewMode('month');
    setDateViewMode('month');
    changeFilter(
      {
        key: 'startDate',
        value: DateUtils.toDateString(current.startOf('month')),
      },
      { key: 'endDate', value: DateUtils.toDateString(current.endOf('month')) },
    );
  }

  return (
    <div
      className={clsx(
        'tw-overflow-hidden tw-transition-all tw-duration-[450ms] tw-ease-[cubic-bezier(0.22,1,0.36,1)]',
        {
          'tw-opacity-100': unrolledWidth,
          'tw-opacity-0': !unrolledWidth,
        },
      )}
      style={{ maxWidth: unrolledWidth ?? 0 }}
    >
      <div ref={contentRef} className="tw-w-max">
        {showMobileView ? (
          <Switch.List>
            <Switch.Tab
              active={gridMobileViewActive}
              onClick={toggleGridMobileView}
              data-name="posts_calendar-view-mode_mobile"
              data-cy="posts_calendar-view-mode_mobile"
              className="tw-whitespace-nowrap tw-px-3"
            >
              {t('mobileView')}
            </Switch.Tab>
          </Switch.List>
        ) : (
          <Switch.List>
            <Switch.Tab
              active={calendarViewMode === 'week'}
              onClick={selectWeek}
              data-name="posts_calendar-view-mode_week"
              data-cy="posts_calendar-view-mode_week"
              className="tw-px-3"
            >
              {t('week')}
            </Switch.Tab>
            <Switch.Tab
              active={calendarViewMode === 'month'}
              onClick={selectMonth}
              data-name="posts_calendar-view-mode_month"
              data-cy="posts_calendar-view-mode_month"
              className="tw-px-3"
            >
              {t('month')}
            </Switch.Tab>
          </Switch.List>
        )}
      </div>
    </div>
  );
};

export default CalendarViewModeSwitch;
